import { type CSSProperties, type ReactNode } from "react";

import { Form } from "antd";
import type { FormItemProps } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiFormItem.less";

export const FormItemLabelAlignments = {
  /** Positions the label on the left of the form item, vertically center aligned */
  left: "left",
  /** Positions the label on the top of the form item */
  top: "top",
  /** Positions the label on the left of the form item, vertically aligned to the top */
  leftTop: "leftTop",
} as const;

export type FormItemLabelAlignments = keyof typeof FormItemLabelAlignments;

/**
 * IBuiFormItemProps is the interface for the BuiFormItem primitive.
 *
 * The props are declared explicitly (rather than extending antd's `FormItemProps`)
 * so the primitive's public surface is decoupled from antd and only exposes the
 * props BDS consumers actually use. If a new antd Form.Item prop is needed, add it
 * here deliberately. antd's native `tooltip` is intentionally excluded so
 * label-tooltip behavior is routed exclusively through `BTFormItem`'s
 * BTPopover-based implementation.
 */
export interface IBuiFormItemProps
  extends Pick<
    FormItemProps,
    | "id"
    | "label"
    | "htmlFor"
    | "name"
    | "help"
    | "extra"
    | "validateStatus"
    | "required"
    | "hidden"
  > {
  /**
   * The alignment of the label within the form item.
   * `top` is above the input
   * `left` is to the left of the input with label text vertically center-aligned,
   * `leftTop` is to the left of the input with label text vertically top-aligned.
   * @default "top"
   */
  labelAlign?: FormItemLabelAlignments;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  "data-testid"?: string;
}

// antd's FormItemProps types `labelAlign` as "left" | "right"; the BDS design uses
// its own vertical/horizontal alignment vocabulary, so the value is mapped to a
// styling modifier class instead of being forwarded to Form.Item.
const LABEL_ALIGN_CLASS: Record<FormItemLabelAlignments, string> = {
  left: "bui-label-align-left",
  top: "bui-label-align-top",
  leftTop: "bui-label-align-left-top",
};

/**
 * BuiFormItem is the Blueprint-styled primitive wrapper around antd's Form.Item.
 * It applies the `bui-form-item`/`bui-theme` classes and the label-alignment
 * modifier for BDS styling, and passes all other supported props through.
 *
 * It is purely presentational; behavior such as tooltips lives in the `BTFormItem`
 * wrapper in `@btnet/core-components`. Prefer `BTFormItem` in app code.
 * @param props - IBuiFormItemProps defining the form item's label, alignment, and content
 * @returns JSX.Element wrapping antd's Form.Item
 * @example
 * <BuiFormItem label="First name" labelAlign="left" htmlFor="first-name">
 *   <BuiInput id="first-name" />
 * </BuiFormItem>
 */
export function BuiFormItem(props: IBuiFormItemProps) {
  const {
    className,
    ariaLabel,
    labelAlign = "top",
    "data-testid": dataTestId = "",
    ...otherProps
  } = props;

  return (
    <Form.Item
      {...otherProps}
      data-testid={dataTestId}
      className={cn(
        "bui-form-item flex relative",
        LABEL_ALIGN_CLASS[labelAlign],
        className,
      )}
      aria-label={ariaLabel}
    />
  );
}

BuiFormItem.displayName = "BuiFormItem";
